import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  inject,
} from '@angular/core';
import { Store } from '@ngxs/store';
import { AuthState } from '@wilson/auth/core';
import {
  GeoLocation,
  OrganizationalUnit,
  TimelineShift,
  TimelineShiftWithActivities,
} from '@wilson/interfaces';
import { LocationsStoreService } from '@wilson/locations';
import {
  DropZoneEventThrottlerDirective,
  GanttDateToPositionPxPipe,
} from '@wilson/shift-timeline/assignments-gantt-chart';
import {
  CreateNewViaDragAndDropService,
  DragAndDropModeService,
  HorizontalDemarcatorRange,
} from '@wilson/shift-timeline/services';
import {
  ShiftTimelineSettingsState,
  TimelineRemoveShiftFromUserV2,
  TimelineStaysSuggestionService,
} from '@wilson/shift-timeline/state';
import { firstValueFrom } from 'rxjs';
import { NewShiftDropZoneHelperService } from './new-shift-drop-zone-helper.service';

@Component({
  selector: 'wilson-new-shift-drop-zone-v2',
  styleUrls: ['./new-shift-drop-zone-component-v2.component.scss'],
  imports: [],
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [GanttDateToPositionPxPipe],
})
export class NewShiftDropZoneV2Component
  extends DropZoneEventThrottlerDirective
  implements OnInit
{
  private store = inject(Store);
  private locationsStoreService = inject(LocationsStoreService);
  private dragAndDropModeService = inject(DragAndDropModeService);
  protected timelineStaysSuggestionService = inject(
    TimelineStaysSuggestionService,
  );
  private newShiftDropZoneHelperService = inject(NewShiftDropZoneHelperService);

  @Input() userId!: string | undefined;

  private OFFSETX_ADJUSTMENT_RATIO = 4;
  private currentDemarcatorRange: HorizontalDemarcatorRange | null = null;
  private organizationalUnit!: OrganizationalUnit | undefined;
  private location!: GeoLocation | null;
  private mockShiftWithoutActivities!: TimelineShift;
  private mockShift!: TimelineShiftWithActivities;

  ngOnInit(): void {
    this.organizationalUnit = this.store.selectSnapshot(
      AuthState.organizationalUnit,
    );
    if (this.organizationalUnit?.locationId) {
      firstValueFrom(
        this.locationsStoreService.getLocationFromCache(
          this.organizationalUnit.locationId as string,
        ),
      ).then((location) => {
        this.location = location || null;
      });
    }
  }

  protected override onDragOverHandler(offsetX: number): void {
    const adjustedOffsetX =
      offsetX -
      this.store.selectSnapshot(ShiftTimelineSettingsState.pixelWidthPerDay()) /
        this.OFFSETX_ADJUSTMENT_RATIO;
    const shiftStartDate =
      this.newShiftDropZoneHelperService.generateDateFromOffsetX(
        adjustedOffsetX,
      );
    const processingResult =
      this.newShiftDropZoneHelperService.handleOnDragOverProcess({
        shiftStartDate,
        offsetX: adjustedOffsetX,
        organizationalUnit: this.organizationalUnit,
        userId: this.userId,
        location: this.location,
      });

    if (processingResult) {
      this.mockShift = processingResult.mockShift;
      this.mockShiftWithoutActivities =
        processingResult.mockShiftWithoutActivities;
      this.currentDemarcatorRange = processingResult.currentDemarcatorRange;
    }
  }

  protected override onDragLeaveHandler(): void {
    if (this.userId) {
      this.store.dispatch(
        new TimelineRemoveShiftFromUserV2({
          userId: this.userId,
          shiftId: CreateNewViaDragAndDropService.MOCK_SHIFT_ID,
        }),
      );
    }
    this.dragAndDropModeService.setDisplayMovingDemarcatorLine(false);
  }

  protected override onDropHandler(): void {
    this.newShiftDropZoneHelperService.handleOnDropProcess({
      currentDemarcatorRange: this.currentDemarcatorRange,
      mockShift: this.mockShift,
      mockShiftWithoutActivities: this.mockShiftWithoutActivities,
      userId: this.userId,
    });
  }
}
